/**
 * Planner Agent - System Prompt Builder
 * 
 * Assembles the Planner agent's system prompt from 5 standardized sections.
 * Each section can be fetched from Keywords AI or fall back to code.
 * 
 * ## Sections
 * 
 * 1. **Intro** - Who the agent is and its goals
 * 2. **Tools** - Tool guidelines (userInterview, alphaSenseSearch, createPlan, editPlan)
 * 3. **Suggestions** - Workflow guidelines and decision trees
 * 4. **Context Note** - Version control and edge case handling
 * 5. **Evaluation** - Communication style and important notes
 */

import { SystemPromptBuilder } from '../../base/systemPrompt';
import { buildToolsSection } from './tools';
import { getIntroSection } from './intro';
import { getSuggestionsSection } from './suggestions';
import { getContextNoteSection } from './contextNote';
import { getEvaluationSection } from './evaluation';

// Re-export section getters
export { getIntroSection } from './intro';
export { getSuggestionsSection } from './suggestions';
export { getContextNoteSection } from './contextNote';
export { getEvaluationSection } from './evaluation';

// Export variables for admin panel and Keywords AI integration
export {
    PLANNER_VARIABLES,
    PLANNER_PROMPT_KEYS,
    PLANNER_PROMPT_CONFIG,
} from './variables';

// Re-export constants for backwards compatibility
export { PLANNER_INTRO } from './intro';
export { PLANNER_SUGGESTIONS } from './suggestions';
export { PLANNER_CONTEXT_NOTE } from './contextNote';
export { PLANNER_EVALUATION } from './evaluation';

// Import the context generators for re-export
import {
    generateCurrentPlanContext,
    generateStyleGuideContext,
} from '../planner.prompt';

// ═══════════════════════════════════════════════════════════════════════════
// PROMPT BUILDER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build the complete system prompt for the Planner agent.
 * Uses Keywords AI prompts if available, otherwise falls back to code.
 * 
 * @param tools - Array of tools available to the agent
 */
export function buildPlannerSystemPrompt(tools: any[]): string {
    const builder = new SystemPromptBuilder();
    
    return builder.build({
        intro: getIntroSection(),
        tools: buildToolsSection(tools),
        suggestions: getSuggestionsSection(),
        contextNote: getContextNoteSection(),
        evaluation: getEvaluationSection(),
    });
}

// Re-export utilities for the agent
export {
    generateCurrentPlanContext,
    generateStyleGuideContext,
};

